// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * countdown.ts — 3 · 2 · 1 · GO (principle #15).
 *
 * A round never begins the instant the board appears, or whoever happened to be
 * looking gets a free head start and the board reads as a jump cut. Each peer
 * counts locally from the moment the host's start arrives (in step to one
 * network hop; the round is host-authoritative anyway).
 *
 * Driven by setInterval, not rAF: a backgrounded tab never fires rAF, and a
 * countdown that stalls there strands a peer in a room that has already begun.
 */

export interface Countdown {
  cancel(): void;
}

export interface CountdownOpts {
  container: HTMLElement;
  onTick?: (n: number) => void;
  onDone: () => void;
  step?: number;
}

export function runCountdown(opts: CountdownOpts): Countdown {
  const { container } = opts;
  const step = opts.step ?? 800;
  let n = 3;
  let timer: number | null = null;
  let done = false;

  const el = document.createElement('div');
  el.className = 'countdown';
  el.setAttribute('aria-live', 'assertive');
  container.appendChild(el);

  const paint = () => {
    el.textContent = n > 0 ? String(n) : 'GO';
    el.classList.remove('pop');
    void el.offsetWidth;
    el.classList.add('pop');
    opts.onTick?.(n);
  };

  const finish = () => {
    if (done) return;
    done = true;
    if (timer !== null) { clearInterval(timer); timer = null; }
    el.remove();
    opts.onDone();
  };

  paint();
  timer = window.setInterval(() => {
    n -= 1;
    if (n < 0) { finish(); return; }
    paint();
  }, step);

  return {
    cancel() {
      if (done) return;
      done = true;
      if (timer !== null) { clearInterval(timer); timer = null; }
      el.remove();
    },
  };
}